const BoxingTierFee = {
  init: async function () {
    this.initCheckoutFormIntercept();

    // Initialize window.boxingFees config


    // Get cart and tiers in parallel
    const [cart, tiers] = await Promise.all([
      this.getCurrentCart(),
      this.getTiers()
    ]);

    window.boxingFees.token = cart.token;
    window.boxingFees.tiers = tiers;

    window.boxingFeesReady = true;

    console.group('🧮 BOXING FEES INITIALISED');
    console.log('📦 Tiers:', window.boxingFees.tiers);
    console.log('🛒 Cart Token:', window.boxingFees.token);
    console.log('🛒 Cart URL:', window.boxingFees.cartUrl);
    console.log('🛒 Preference:', window.boxingFees.preference);
    console.log('🛒 Message:', window.boxingFees.message);
    console.log('🛒 Storefront API URL:', window.boxingFees.storefrontApiUrl);
    console.groupEnd();
  },

  /**
   * Initialize the checkout form intercept to ensure box fee is added before checkout
   * We also watch for cart count updates to ensure the form listeners are attached when the cart count changes
   * This is because the cart flyout on add to cart renders a new form in the DOM that we need to listen to
   */
  initCheckoutFormIntercept: function () {
    // Store reference to bound handler to allow removal
    this.cartUpdateHandler = () => {
      this.attachFormListeners();
    };

    // Initial attachment of listeners
    this.cartUpdateHandler();

    // Watch cart count bubble for updates using MutationObserver
    const cartCountBubble = document.querySelector('[data-header-cart-count]');
    if (cartCountBubble) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'data-header-cart-count') {
            // Wait for DOM to update with new cart flyout
            setTimeout(this.cartUpdateHandler, 500);
          }
        });
      });

      observer.observe(cartCountBubble, {
        attributes: true,
        attributeFilter: ['data-header-cart-count']
      });
    }
  },

  attachFormListeners: function () {
    // Check if we've already set up the global listener
    if (document.body.dataset.boxingFeeGlobalListener) {
      return;
    }

    const handleBoxFeeUpdates = async (boxFee) => {
      if (boxFee.remove.length > 0) {
        await BoxingTierFee.removeLineItems(boxFee.remove);
      }

      if (boxFee.add.length > 0) {
        await BoxingTierFee.addLineItems(boxFee.add);
        console.log('🛒 Added fee tiers to cart because the user is checking out');
      }
    };

    // Use event delegation on document body to catch all form submissions
    const globalSubmitHandler = async function (event) {
      // Check if the event target is a checkout form
      const form = event.target.closest('form[action="/cart"], .atc-banner__form[action="/cart"]');
      if (!form) return;

      // Check if this is a submit event or click on a submit button
      const isSubmitEvent = event.type === 'submit';
      const isSubmitButton = event.type === 'click' &&
        (event.target.type === 'submit' ||
          event.target.matches('button[type="submit"], input[type="submit"]'));

      if (!isSubmitEvent && !isSubmitButton) return;

      event.preventDefault();
      event.stopPropagation();

      // Prevent duplicate submissions using a form-specific flag
      const formId = form.action + (form.className || '');
      const flagName = `boxingFeeSubmitting_${btoa(formId).slice(0, 10)}`;

      if (window[flagName]) {
        return;
      }

      window[flagName] = true;
      console.log('🛒 Form submitted (via delegation), calculating box fee');

      try {
        const enrichedCart = await BoxingTierFee.getEnrichedCart();
        const boxFee = BoxingTierFee.calculateBoxFee(window.boxingFees.tiers, enrichedCart);
        await handleBoxFeeUpdates(boxFee);

        // Finally push the client to checkout
        window.location = "/checkout";
      } catch (error) {
        console.error('Error during checkout:', error);
        window[flagName] = false;
      }
    };

    // Attach global listeners for both submit and click events
    document.body.addEventListener('submit', globalSubmitHandler, true); // Use capture phase
    document.body.addEventListener('click', globalSubmitHandler, true);   // Use capture phase

    // Mark that we've set up the global listener
    document.body.dataset.boxingFeeGlobalListener = 'true';

    console.log('🛒 Global form delegation listeners attached');
  },

  /**
   * Send a storefront API request
   * @param {string} query - The query to send
   * @param {Object} variables - Optional variables to send with the query
   * @returns {Promise<Object>} The response from the API
   */
  sendStorefrontApiRequest: async function (query, variables) {
    const response = await fetch(window.boxingFees.storefrontApiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": window.boxingFees.metaobjectStorefrontApiToken,
      },
      body: JSON.stringify({ query, variables: variables || undefined }),
    });
    return response.json();
  },

  /**
   * Remove multiple line items from the cart at once
   * @param {Array} lineItemIds - Array of line item IDs to remove
   * @returns {Promise<Object>} The response from the API
   */
  removeLineItems: async function (lineItemIds) {
    if (!lineItemIds || lineItemIds.length === 0) {
      return { success: true };
    }

    // Create updates object with all items set to quantity 0
    const updates = {};
    lineItemIds.forEach(id => {
      updates[id.toString()] = 0;
    });

    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ updates })
    });

    if (!response.ok) {
      console.error('Error removing line items:', response.statusText);
      throw new Error('Failed to remove items from cart');
    }

    return response.json();
  },

  /**
   * Add variant(s) to the cart
   * @param {string|Array} variantIdOrItems - Either a single variant ID or an array of items
   * @param {number} quantity - The quantity to add (used only for single variant)
   * @param {Array} attributes - Optional attributes to add to the line item (used only for single variant)
   * @returns {Promise<Object>} The response from the API
   */
  addLineItems: async function (variantIdOrItems, quantity = 1, attributes = []) {
    let requestBody;

    // Check if first parameter is an array (bulk addition)
    if (Array.isArray(variantIdOrItems)) {
      // Bulk addition using items array
      requestBody = {
        items: variantIdOrItems.map(item => ({
          id: item.variantId.replace('gid://shopify/ProductVariant/', ''),
          quantity: item.quantity || 1,
          properties: item.attributes ? item.attributes.reduce((acc, attr) => {
            acc[attr.key] = attr.value;
            return acc;
          }, {}) : {}
        }))
      };
    } else {
      // Single addition (backward compatibility)
      requestBody = {
        items: [{
          id: variantIdOrItems.replace('gid://shopify/ProductVariant/', ''),
          quantity,
          properties: attributes.reduce((acc, attr) => {
            acc[attr.key] = attr.value;
            return acc;
          }, {})
        }]
      };
    }

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      console.error('Error adding variant(s) to cart:', response.statusText);
      throw new Error('Failed to add item(s) to cart');
    }

    return response.json();
  },

  /**
   * Find the current fee tier in the cart and match it with the corresponding tier configuration
   * @returns {Promise<Array>} Array containing current fee tiers and their configurations
   */
  findCurrentFeeTierInCart: async function () {
    const enrichedCart = await this.getEnrichedCart();
    const boxingTiers = window.boxingFees.tiers;

    // Initialize result array
    const result = [];

    // Loop through enriched cart items to find matching tiers
    for (const item of enrichedCart) {
      const matchingTier = boxingTiers.find(tier => tier.variantId === item.variantId);

      if (matchingTier) {
        result.push({
          ...matchingTier,
          lineItemId: item.id, // storefront line item id
          cartItemId: item.cartItemId, // cart.json item id
          cartKey: item.cartKey, // cart.json key
          quantity: item.quantity
        });
      }
    }

    return result;
  },

  /**
   * List all fee tiers
   * @returns {Promise<Array>}
   */
  listFeeTiers: async function () {
    let tiers = [];
    let hasNextPage = true;
    let endCursor = null;
    while (hasNextPage) {
      const response = await this.sendStorefrontApiRequest(this.listFeeTiersQuery, {
        after: endCursor,
      });

      // Check if response has expected data structure
      if (!response.data?.metaobjects?.pageInfo) {
        console.error('Invalid response structure:', response);
        break;
      }

      // Add the edges to tiers array if they exist
      if (response.data.metaobjects.edges) {
        tiers.push(...response.data.metaobjects.edges);
      }

      // Update pagination only if there are more pages and we have a cursor
      hasNextPage = response.data.metaobjects.pageInfo.hasNextPage && response.data.metaobjects.pageInfo.endCursor;
      endCursor = response.data.metaobjects.pageInfo.endCursor;
    }
    return tiers.map(tier => {
      if (!tier?.node?.product?.reference) return null;
      return {
        id: tier.node.id,
        maxLength: parseInt(tier.node.maxLength.value),
        maxPieces: parseInt(tier.node.maxPieces.value),
        price: JSON.parse(tier.node.price.value),
        productId: tier.node.product.reference.id,
        variantId: tier.node.product.reference.variants.nodes[0].id,
      };
    }).filter(Boolean);
  },

  /**
   * Get fee tiers with caching
   * @returns {Promise<Array>} Cached tiers or freshly fetched tiers
   */
  getTiers: async function () {
    // Return cached tiers if available
    if (window.boxingFees?.tiers) {
      return window.boxingFees.tiers;
    }

    // Otherwise fetch tiers
    return await this.listFeeTiers();
  },

  /**
   * Get the current cart
   * @returns {Promise<Object>} The current cart
   */
  getCurrentCart: async function () {
    const response = await fetch(window.boxingFees.cartUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    const cart = await response.json();
    return cart;
  },

  /**
   * Get the enriched cart with product metafields
   * @returns {Promise<Object>} The enriched cart
   */
  getEnrichedCart: async function () {
    // Get both cart.json and storefront cart
    const cartJson = await this.getCurrentCart();
    const token = window.boxingFees.token;
    const cartId = `gid://shopify/Cart/${token}`;

    const response = await this.sendStorefrontApiRequest(this.getCartWithProductMetafieldsQuery, {
      cartId,
    });

    if (!response.data?.cart) {
      console.error('Invalid response structure:', response);
      return null;
    }

    // Merge storefront data with cart.json data
    return response.data.cart.lines.edges.map(line => {
      const { merchandise, quantity, id: storefrontLineItemId } = line.node;
      const { id: variantId, title, length, pieces, product } = merchandise;

      // Find matching item in cart.json (match by variant_id and quantity)
      const cartJsonItem = cartJson.items.find(item =>
        item.variant_id.toString() === variantId.replace('gid://shopify/ProductVariant/', '') &&
        item.quantity === quantity
      );

      return {
        id: storefrontLineItemId, // storefront line item id (for reference)
        cartItemId: cartJsonItem?.id, // cart.json item id for AJAX removal operations
        cartKey: cartJsonItem?.key, // cart.json key for AJAX operations
        title,
        length: length ? JSON.parse(length.value) : null,
        pieces: pieces ? JSON.parse(pieces.value) : null,
        quantity,
        variantId,
        productId: product.id
      };
    });
  },

  /**
   * Calculate box fee based on cart contents and available tiers
   * @param {Array} tiers - List of box price tiers
   * @param {Array} cartItems - List of line items in the current cart
   * @returns {Object} Object containing remove and add arrays for box fee products
   */
  calculateBoxFee: function (tiers, cartItems) {
    let output = {
      remove: [],
      add: []
    };

    // Filter out items without both pieces and length
    const qualifyingItems = cartItems.filter(item => item.pieces && item.length);

    // Find current box fee products in cart
    const currentBoxFees = cartItems.filter(item =>
      tiers.some(tier => tier.variantId === item.variantId)
    );

    if (qualifyingItems.length === 0) {
      currentBoxFees.forEach(fee => fee.cartItemId && output.remove.push(fee.cartItemId));
      return output;
    }

    // Calculate total pieces and get all lengths
    const totalPieces = qualifyingItems.reduce((sum, item) => {
      const piecesValue = typeof item.pieces === 'number' ? item.pieces : item.pieces.value;
      return sum + (piecesValue * item.quantity);
    }, 0);

    // Get array of all lengths
    const lengths = qualifyingItems.map(item => {
      return typeof item.length === 'number' ? item.length : item.length.value;
    });

    // Sort lengths in descending order
    lengths.sort((a, b) => b - a);

    // If we have multiple lengths, check if total pieces can fit in box for larger length
    let effectiveLength = lengths[0]; // Start with largest length
    if (lengths.length > 1) {
      // Find tier that can accommodate total pieces at largest length
      const tierForTotalPieces = tiers.find(tier =>
        tier.maxPieces >= totalPieces && tier.maxLength >= lengths[0]
      );

      // If such a tier exists, use the largest length
      if (tierForTotalPieces) {
        effectiveLength = lengths[0];
      } else {
        // Otherwise use second largest length if it exists
        effectiveLength = lengths[1] || lengths[0];
      }
    }

    // Find all tiers that can accommodate the effective length
    const availableTiers = tiers.filter(tier => tier.maxLength >= effectiveLength);
    if (availableTiers.length === 0) {
      // No tier can accommodate the length, remove any existing box fee
      currentBoxFees.forEach(fee => fee.cartItemId && output.remove.push(fee.cartItemId));
      return output;
    }

    // Sort tiers by price (ascending) for optimization
    availableTiers.sort((a, b) => parseFloat(a.price.amount) - parseFloat(b.price.amount));

    // Find the optimal combination based on merchant preference
    let optimalCombination;
    const preference = window.boxingFees.preference || 'shortest';

    switch (preference) {
      case 'fewest':
        optimalCombination = this.findFewestBoxCombination(totalPieces, availableTiers);
        break;
      case 'cheapest':
        optimalCombination = this.findCheapestBoxCombination(totalPieces, availableTiers);
        break;
      case 'shortest':
      default:
        optimalCombination = this.findShortestBoxCombination(totalPieces, availableTiers);
        break;
    }

    if (!optimalCombination || optimalCombination.length === 0) {
      currentBoxFees.forEach(fee => fee.cartItemId && output.remove.push(fee.cartItemId));
      return output;
    }

    // Check if current cart already has the optimal combination
    const currentMatches = this.compareCombinations(currentBoxFees, optimalCombination, tiers);
    if (currentMatches) {
      console.log('🛒 Current cart already has the optimal combination, no changes needed');
      return output;
    }

    // Return changes needed
    currentBoxFees.forEach(fee => fee.cartItemId && output.remove.push(fee.cartItemId));
    output.add = optimalCombination;
    return output;
  },

  /**
   * Find the cheapest combination of boxes to fit the given pieces
   * @param {number} totalPieces - Total pieces to fit
   * @param {Array} availableTiers - Tiers that can accommodate the length
   * @returns {Array} Array of box combinations with variantId, quantity, and tier info
   */
  findCheapestBoxCombination: function (totalPieces, availableTiers) {
    // Try different combinations using dynamic programming approach
    const dp = new Array(totalPieces + 1).fill(null);
    dp[0] = { cost: 0, combination: [] };

    for (let pieces = 1; pieces <= totalPieces; pieces++) {
      for (const tier of availableTiers) {
        // We can use this tier if it can hold at least 1 piece and we have pieces to put in it
        const piecesToPutInThisTier = Math.min(pieces, tier.maxPieces);
        const remainingPieces = pieces - piecesToPutInThisTier;

        if (dp[remainingPieces] !== null) {
          const cost = dp[remainingPieces].cost + parseFloat(tier.price.amount);
          if (dp[pieces] === null || cost < dp[pieces].cost) {
            // Create new combination by adding this tier
            const newCombination = [...dp[remainingPieces].combination];
            const existingTier = newCombination.find(item => item.variantId === tier.variantId);

            const attributes = window.boxingFees.message.enabled === true ? [
              {
                key: window.boxingFees.message.prefix,
                value: window.boxingFees.message.body.replace('{tier.maxLength}', tier.maxLength).replace('{tier.maxPieces}', tier.maxPieces)
              }
            ] : undefined;

            if (existingTier) {
              existingTier.quantity++;
            } else {
              newCombination.push({
                variantId: tier.variantId,
                quantity: 1,
                maxLength: tier.maxLength,
                maxPieces: tier.maxPieces,
                attributes
              });
            }

            dp[pieces] = {
              cost: cost,
              combination: newCombination
            };
          }
        }
      }
    }

    return dp[totalPieces] ? dp[totalPieces].combination : null;
  },

  /**
   * Find the shortest combination of boxes to fit the given pieces (prioritizes smaller box lengths)
   * @param {number} totalPieces - Total pieces to fit
   * @param {Array} availableTiers - Tiers that can accommodate the length
   * @returns {Array} Array of box combinations with variantId, quantity, and tier info
   */
  findShortestBoxCombination: function (totalPieces, availableTiers) {
    // Sort tiers by length (ascending) then by price (ascending) as tiebreaker
    const sortedTiers = [...availableTiers].sort((a, b) => {
      if (a.maxLength !== b.maxLength) {
        return a.maxLength - b.maxLength; // Shorter boxes first
      }
      return parseFloat(a.price.amount) - parseFloat(b.price.amount); // Cheaper as tiebreaker
    });

    // Use dynamic programming but prioritize shorter lengths
    const dp = new Array(totalPieces + 1).fill(null);
    dp[0] = { maxLength: 0, cost: 0, combination: [] };

    for (let pieces = 1; pieces <= totalPieces; pieces++) {
      for (const tier of sortedTiers) {
        const piecesToPutInThisTier = Math.min(pieces, tier.maxPieces);
        const remainingPieces = pieces - piecesToPutInThisTier;

        if (dp[remainingPieces] !== null) {
          const newMaxLength = Math.max(dp[remainingPieces].maxLength, tier.maxLength);
          const newCost = dp[remainingPieces].cost + parseFloat(tier.price.amount);

          // Prefer combination with shorter max length, or cheaper cost as tiebreaker
          const shouldUpdate = dp[pieces] === null ||
            newMaxLength < dp[pieces].maxLength ||
            (newMaxLength === dp[pieces].maxLength && newCost < dp[pieces].cost);

          const attributes = window.boxingFees.message.enabled === true ? [
            {
              key: window.boxingFees.message.prefix,
              value: window.boxingFees.message.body.replace('{tier.maxLength}', tier.maxLength).replace('{tier.maxPieces}', tier.maxPieces)
            }
          ] : undefined;

          if (shouldUpdate) {
            const newCombination = [...dp[remainingPieces].combination];
            const existingTier = newCombination.find(item => item.variantId === tier.variantId);
            if (existingTier) {
              existingTier.quantity++;
            } else {
              newCombination.push({
                variantId: tier.variantId,
                quantity: 1,
                maxLength: tier.maxLength,
                maxPieces: tier.maxPieces,
                attributes
              });
            }

            dp[pieces] = {
              maxLength: newMaxLength,
              cost: newCost,
              combination: newCombination
            };
          }
        }
      }
    }

    return dp[totalPieces] ? dp[totalPieces].combination : null;
  },

  /**
   * Find the combination with fewest number of boxes to fit the given pieces
   * @param {number} totalPieces - Total pieces to fit
   * @param {Array} availableTiers - Tiers that can accommodate the length
   * @returns {Array} Array of box combinations with variantId, quantity, and tier info
   */
  findFewestBoxCombination: function (totalPieces, availableTiers) {
    // Sort tiers by capacity (descending) then by price (ascending) as tiebreaker
    const sortedTiers = [...availableTiers].sort((a, b) => {
      if (a.maxPieces !== b.maxPieces) {
        return b.maxPieces - a.maxPieces; // Higher capacity first
      }
      return parseFloat(a.price.amount) - parseFloat(b.price.amount); // Cheaper as tiebreaker
    });

    // Use dynamic programming to minimize number of boxes
    const dp = new Array(totalPieces + 1).fill(null);
    dp[0] = { boxCount: 0, cost: 0, combination: [] };

    for (let pieces = 1; pieces <= totalPieces; pieces++) {
      for (const tier of sortedTiers) {
        const piecesToPutInThisTier = Math.min(pieces, tier.maxPieces);
        const remainingPieces = pieces - piecesToPutInThisTier;

        if (dp[remainingPieces] !== null) {
          const newBoxCount = dp[remainingPieces].boxCount + 1;
          const newCost = dp[remainingPieces].cost + parseFloat(tier.price.amount);

          // Prefer combination with fewer boxes, or cheaper cost as tiebreaker
          const shouldUpdate = dp[pieces] === null ||
            newBoxCount < dp[pieces].boxCount ||
            (newBoxCount === dp[pieces].boxCount && newCost < dp[pieces].cost);

          if (shouldUpdate) {
            const newCombination = [...dp[remainingPieces].combination];
            const existingTier = newCombination.find(item => item.variantId === tier.variantId);
            if (existingTier) {
              existingTier.quantity++;
            } else {
              newCombination.push({
                variantId: tier.variantId,
                quantity: 1,
                maxLength: tier.maxLength,
                maxPieces: tier.maxPieces,
                attributes
              });
            }

            dp[pieces] = {
              boxCount: newBoxCount,
              cost: newCost,
              combination: newCombination
            };
          }
        }
      }
    }

    return dp[totalPieces] ? dp[totalPieces].combination : null;
  },

  /**
   * Compare current box fees in cart with optimal combination
   * @param {Array} currentBoxFees - Current box fee items in cart
   * @param {Array} optimalCombination - Optimal combination to compare against
   * @param {Array} tiers - All available tiers
   * @returns {boolean} True if combinations match
   */
  compareCombinations: function (currentBoxFees, optimalCombination, tiers) {
    if (currentBoxFees.length !== optimalCombination.length) {
      return false;
    }

    // Create maps for easier comparison
    const currentMap = new Map();
    currentBoxFees.forEach(fee => {
      currentMap.set(fee.variantId, fee.quantity);
    });

    const optimalMap = new Map();
    optimalCombination.forEach(item => {
      optimalMap.set(item.variantId, item.quantity);
    });

    // Compare the maps
    if (currentMap.size !== optimalMap.size) {
      return false;
    }

    for (const [variantId, quantity] of currentMap) {
      if (optimalMap.get(variantId) !== quantity) {
        return false;
      }
    }

    return true;
  },

  /**
   * GRAPHQL QUERIES
   */
  getCartWithProductMetafieldsQuery: `
    query($cartId: ID!) {
      cart(id: $cartId) {
        id
        lines(first: 250) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  length: metafield(namespace: "custom", key: "actual_length") {
                    value
                  }
                  pieces: metafield(namespace: "custom", key: "pieces") {
                    value
                  }
                  product {
                    id
                    title
                  }
                }
              }
            }
          }
        }
      }
    }
  `,

  listFeeTiersQuery: `
    query($after: String) {
      metaobjects(first: 100, type: "boxing_fee_tier", after: $after) {
        pageInfo {
          hasPreviousPage
          hasNextPage
          startCursor
          endCursor
        }
        edges {
          node {
            id
            maxLength: field(key: "max_length") {
              value
            }
            maxPieces: field(key: "max_pieces") {
              value
            }
            price: field(key: "price") {
              value
            }
            product: field(key: "auto_associated_product") {
              reference {
                ... on Product {
                  id
                  variants(first: 1) {
                    nodes {
                      id
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `,

  cartLinesRemoveMutation: `
    mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
      cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
        cart {
          id
          lines(first: 250) {
            edges {
              node {
                id
                quantity
                merchandise {
                  ... on ProductVariant {
                    id
                    title
                  }
                }
              }
            }
          }
          userErrors {
            field
            message
          }
        }
      }
    }
  `,

  cartLinesAddMutation: `
    mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
      cartLinesAdd(cartId: $cartId, lines: $lines) {
        cart {
          id
          lines(first: 250) {
            edges {
              node {
                id
                quantity
                merchandise {
                  ... on ProductVariant {
                    id
                    title
                  }
                }
              }
            }
          }
          userErrors {
            field
            message
          }
        }
      }
    }
  `,
};

document.addEventListener("DOMContentLoaded", async function () {
  await BoxingTierFee.init();

  const currentFeeTier = await BoxingTierFee.findCurrentFeeTierInCart();

  // Remove any existing fee tiers from cart on page load
  if (currentFeeTier.length > 0) {
    const lineItemIds = currentFeeTier.map(tier => tier.cartItemId).filter(Boolean);

    if (lineItemIds.length > 0) {
      await BoxingTierFee.removeLineItems(lineItemIds);
      console.log('🛒 Removed existing fee tiers from cart while the user browses the site');
    }
  }

});
